import { useState } from "react";
import {
  CURSE_KINDS,
  CURSE_NAMES,
  HECKLES,
  benchPoints,
  cursesNow,
  curseLine,
  curseName,
  curseProblem,
  heckleText,
  pickLineup,
  pickOutcome,
  pickProblem,
  picksOf,
  pickTally,
} from "../../../../engine/bench";
import { currentRound, displayName } from "../../../../engine/selectors";
import type { CourtGame, CurseKind, Lineup, PlayerId, TournamentState } from "../../../../engine/types";
import type { Tournament } from "../../lib/useTournament";
import { mmss } from "../court/clock";
import { useRemembered } from "./remember";
import { lengthOf, useSent } from "./sent";

type Tab = "heckle" | "curse" | "pick";
/** Which fold is open, "none" for all three shut. Shut is how it starts. */
type Open = Tab | "none";
const OPEN: readonly Open[] = ["none", "heckle", "curse", "pick"];
type Sent = ReturnType<typeof useSent>;
type Side = "a" | "b";
const SIDES: Side[] = ["a", "b"];

/** The engine's reasons are written lower case, to follow "Not sent:". Alone they are a sentence. */
function sentence(reason: string): string {
  return reason[0]!.toUpperCase() + reason.slice(1) + ".";
}

function pairName(state: TournamentState, ids: readonly PlayerId[]): string {
  return ids.map((id) => displayName(state.players[id], "phone")).join(" & ");
}

function inLineup(lineup: Lineup, id: PlayerId): boolean {
  return lineup.courts.some((c) => c.a.includes(id) || c.b.includes(id));
}

/** A row of courts to pick from, as round badges in each court's colour. */
function Courts({
  state,
  chosen,
  onChoose,
  everyone,
}: {
  state: TournamentState;
  chosen: number | null | undefined;
  onChoose(court: number | null): void;
  /** Offer "everyone" too (a heckle at the hall). */
  everyone?: boolean;
}) {
  const courts = Array.from({ length: state.config.courts }, (_, i) => i + 1);
  return (
    <div className="bench-courts">
      {courts.map((c) => (
        <button
          key={c}
          type="button"
          className={`bench-court court-${c}` + (chosen === c ? " on" : "")}
          aria-pressed={chosen === c}
          onClick={() => onChoose(c)}
        >
          <small>Court</small>
          <strong>{c}</strong>
        </button>
      ))}
      {everyone && (
        <button
          type="button"
          className={"bench-court bench-everyone" + (chosen === null ? " on" : "")}
          aria-pressed={chosen === null}
          onClick={() => onChoose(null)}
        >
          Everyone
        </button>
      )}
    </div>
  );
}

/** The court's number on its colour: the left end of a court's row. */
function CourtNo({ court }: { court: number }) {
  return (
    <span className="bench-match-no">
      <small>Court</small>
      <strong>{court}</strong>
    </span>
  );
}

/** A pair's two names, one above the other, so two pairs sit side by side on a narrow phone. */
function PairNames({ state, ids }: { state: TournamentState; ids: readonly PlayerId[] }) {
  return (
    <span className="bench-pair-names">
      {ids.map((id, i) => (
        <span key={id}>
          {displayName(state.players[id], "phone")}
          {i < ids.length - 1 ? " &" : ""}
        </span>
      ))}
    </span>
  );
}

/** A court as one compact card: the number on its colour, its two pairs side by side to tap. */
function Match({
  state,
  game,
  chosen,
  split,
  onChoose,
}: {
  state: TournamentState;
  game: CourtGame;
  chosen?: Side | undefined;
  /** How the hall picked, shown on the two blocks. */
  split?: { a: number; b: number } | undefined;
  onChoose(side: Side): void;
}) {
  return (
    <div className={`bench-match court-${game.court}`}>
      <CourtNo court={game.court} />
      <div className="bench-match-pairs">
        {SIDES.map((k) => (
          <button
            key={k}
            type="button"
            className={"bench-pair" + (chosen === k ? " on" : "")}
            aria-pressed={chosen === k}
            aria-label={pairName(state, game[k]) + (split ? `, ${split[k]} picked them` : "")}
            onClick={() => onChoose(k)}
          >
            <PairNames state={state} ids={game[k]} />
            {split && <b>{split[k]}</b>}
          </button>
        ))}
      </div>
    </div>
  );
}

function Heckle({
  state,
  me,
  now,
  sent,
  onCourt,
  played,
}: {
  state: TournamentState;
  me: PlayerId;
  now: number;
  sent: Sent;
  onCourt: boolean;
  /** My game's score is in, but the round goes on. */
  played: boolean;
}) {
  const [court, setCourt] = useState<number | null | undefined>(undefined);
  const [line, setLine] = useState<number | null>(null);
  const [text, setText] = useState("");
  const player = state.players[me];
  const max = state.config.bench.heckleMaxChars;
  const wait = player?.lastHeckleAt != null ? player.lastHeckleAt + state.config.bench.heckleEveryMs - now : 0;
  const ready = court !== undefined && lengthOf(text) > 0 && !sent.waiting;

  if (onCourt) {
    return (
      <div className="bench-body">
        <p className="bench-wait">{played ? "Your game is done. Heckle when the round is over." : "You are on court. Heckle when you are resting."}</p>
      </div>
    );
  }

  if (wait > 0) {
    const mine = state.bench.heckles.filter((h) => h.by === me);
    const last = mine[mine.length - 1];
    return (
      <div className="bench-body">
        {last && <p className="bubble">{last.text}</p>}
        <p className="bench-wait">
          Sent to the wall. Next heckle in <strong>{mmss(wait)}</strong>.
        </p>
      </div>
    );
  }

  return (
    <div className="bench-body">
      <p className="bench-ask">Who gets it</p>
      <Courts
        state={state}
        chosen={court}
        everyone
        onChoose={(c) => {
          setCourt(c);
          setLine(null);
        }}
      />
      {court !== undefined && (
        <>
          <p className="bench-ask">Say your own</p>
          <form
            className="bench-type"
            onSubmit={(e) => {
              e.preventDefault();
              if (ready) sent.send({ type: "heckle.sent", player: me, court, text });
            }}
          >
            <input
              type="text"
              value={text}
              placeholder="Type a heckle"
              aria-label="Type a heckle"
              autoComplete="off"
              enterKeyHint="send"
              onChange={(e) => setText([...e.target.value].slice(0, max).join(""))}
              onFocus={() => setLine(null)}
            />
            <button type="submit" className="btn accent" disabled={!ready}>
              Send
            </button>
            <span className="join-count">
              {[...text].length}/{max}
            </span>
          </form>
          <p className="bench-ask">Or pick one</p>
          <ul className="bench-lines">
            {HECKLES.map((_, i) => (
              <li key={i} className={line === i ? "on" : ""}>
                <button type="button" className="bench-line" aria-pressed={line === i} onClick={() => setLine(line === i ? null : i)}>
                  {heckleText(i, court)}
                </button>
                {line === i && (
                  <button
                    type="button"
                    className="btn accent"
                    disabled={sent.waiting}
                    onClick={() => sent.send({ type: "heckle.sent", player: me, court, line: i })}
                  >
                    Send to the wall
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function Curse({
  state,
  me,
  now,
  sent,
  played,
}: {
  state: TournamentState;
  me: PlayerId;
  now: number;
  sent: Sent;
  /** My game's score is in, but the round goes on. */
  played: boolean;
}) {
  const [aim, setAim] = useState<{ roundNo: number; court: number; side: Side } | null>(null);
  const [kind, setKind] = useState<CurseKind | null>(null);
  const running = cursesNow(state, now);
  const mine = running.find((c) => c.by === me);
  const round = currentRound(state);

  if (mine) {
    const game = round?.games.find((g) => g.court === mine.court);
    return (
      <div className="bench-body">
        <div className={`bench-running court-${mine.court}`}>
          <p>
            Your curse is on court {mine.court}, <strong>{mmss(mine.leftMs)}</strong>
          </p>
          {game && (
            <p className="bench-running-who">
              {mine.stepsOff
                ? `${pairName(state, game[mine.side])}: solo, ${pairName(state, game[mine.side].filter((id) => id !== mine.stepsOff))} plays alone.`
                : `${pairName(state, game[mine.side])}: ${curseName(mine).toLowerCase()}.`}
            </p>
          )}
        </div>
      </div>
    );
  }

  const problem = curseProblem(state, me, now);
  if (problem || !round) {
    // the engine says "on court" until the round ends; with my score in, say it as the Heckle fold does
    const words =
      played && problem === "you are on court; curse when you are resting"
        ? "Your game is done. Curse when you are resting in a round."
        : sentence(problem ?? "curses are for a round being played");
    return (
      <div className="bench-body">
        <p className="bench-wait">{words}</p>
      </div>
    );
  }

  const games = round.lineup.courts.filter((c) => {
    const g = round.games.find((x) => x.court === c.court);
    return g && !g.score && !g.voided;
  });
  const target = aim && aim.roundNo === round.no ? games.find((g) => g.court === aim.court) : undefined;
  const seconds = Math.round(state.config.bench.curseMs / 1000);

  if (aim && target) {
    return (
      <div className="bench-body">
        <div className={`bench-confirm court-${aim.court}`}>
          <p className="bench-confirm-ask">Curse {pairName(state, target[aim.side])}?</p>
          <div className="curse-kinds" role="radiogroup" aria-label="Which curse">
            {CURSE_KINDS.map((k) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={kind === k}
                className={"bench-pair curse-kind" + (kind === k ? " on" : "")}
                onClick={() => setKind(k)}
              >
                <strong>{CURSE_NAMES[k]}</strong>
                <small>{curseLine(k, seconds)}</small>
              </button>
            ))}
          </div>
          <p>You get one all day.</p>
          <div className="join-actions">
            <button
              type="button"
              className="btn quiet"
              onClick={() => {
                setAim(null);
                setKind(null);
              }}
            >
              No
            </button>
            <button
              type="button"
              className="btn accent"
              disabled={sent.waiting || !kind}
              onClick={() => kind && sent.send({ type: "curse.thrown", player: me, court: aim.court, side: aim.side, kind })}
            >
              Curse them
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bench-body">
      <p>Tap a pair, then pick the curse. It lasts {seconds} seconds.</p>
      {games.length === 0 && <p className="bench-wait">Every game this round is over.</p>}
      {games.map((g) => {
        const taken = running.find((c) => c.court === g.court);
        return taken ? (
          <div key={g.court} className={`bench-match court-${g.court}`}>
            <CourtNo court={g.court} />
            <p className="bench-match-line">Already cursed, {mmss(taken.leftMs)} left.</p>
          </div>
        ) : (
          <Match key={g.court} state={state} game={g} onChoose={(side) => setAim({ roundNo: round.no, court: g.court, side })} />
        );
      })}
    </div>
  );
}

/** One round's courts to pick: tap a pair, tap the other to change. */
function PickRound({
  state,
  me,
  roundNo,
  lineup,
  heading,
  sent,
}: {
  state: TournamentState;
  me: PlayerId;
  roundNo: number;
  lineup: Lineup;
  heading: string;
  sent: Sent;
}) {
  const mine = picksOf(state, me, roundNo);
  const tally = pickTally(state, roundNo);
  const round = state.rounds[roundNo - 1];
  const courts = lineup.courts.filter((c) => {
    const g = round?.games.find((x) => x.court === c.court);
    return !g || (!g.score && !g.voided);
  });
  if (!courts.length) return null;
  return (
    <section className="bench-round">
      <h3>{heading}</h3>
      {courts.map((c) => {
        const chosen = mine.find((p) => p.court === c.court)?.side;
        return (
          <Match
            key={c.court}
            state={state}
            game={c}
            chosen={chosen}
            split={chosen ? (tally.get(c.court) ?? { a: 0, b: 0 }) : undefined}
            onChoose={(side) => {
              if (side !== chosen) sent.send({ type: "pick.made", player: me, roundNo, court: c.court, side });
            }}
          />
        );
      })}
    </section>
  );
}

/** A round that is closed for picks: what I picked, and how each one went once its score is in. */
function PickedRound({ state, me, roundNo, why }: { state: TournamentState; me: PlayerId; roundNo: number; why: string }) {
  const mine = state.bench.picks.filter((p) => p.by === me && p.roundNo === roundNo).sort((x, y) => x.court - y.court);
  return (
    <section className="bench-round">
      <h3>Round {roundNo}</h3>
      <p>{why}</p>
      {mine.map((pick) => {
        const outcome = pickOutcome(state, pick);
        return (
          <div key={pick.court} className={`bench-match court-${pick.court}`}>
            <CourtNo court={pick.court} />
            <div className="bench-match-pairs one">
              <p className="bench-pair on">
                <PairNames state={state} ids={pick.pair} />
                {outcome !== null && <b className={outcome ? "right" : "wrong"}>{outcome ? "right" : "wrong"}</b>}
              </p>
            </div>
          </div>
        );
      })}
      {mine.length === 0 && <p className="bench-wait">You made no picks this round.</p>}
    </section>
  );
}

/** What the Pick tab has for this player right now: rounds to pick, and the latest one's picks once it is closed. */
function pickRounds(state: TournamentState, me: PlayerId, now: number) {
  const round = currentRound(state);
  const playing = round && state.clock.phase === "playing" && state.clock.roundNo === round.no ? round : null;
  const resting = round ? !inLineup(round.lineup, me) : false;
  const problem = playing ? pickProblem(state, playing.no, now) : null;
  const nextNo = state.rounds.length + 1;
  const preview = pickLineup(state, nextNo);
  return {
    /** The round being played, while it can be picked and I am resting. */
    live: playing && resting && !problem ? playing : null,
    /** The latest round, closed: mine to read if I rested in it. */
    closed: round && resting && (!playing || problem) ? round : null,
    why: problem === "everything is paused" ? "Everything is paused." : "Picks closed at half time.",
    nextNo,
    next: preview && !inLineup(preview, me) ? preview : null,
  };
}

function Picks({ state, me, now, sent }: { state: TournamentState; me: PlayerId; now: number; sent: Sent }) {
  const { live, closed, why, nextNo, next } = pickRounds(state, me, now);
  const points = benchPoints(state).get(me);
  const from = state.config.bench.pickStreakFrom;
  const streak = points?.streak ?? 0;
  return (
    <div className="bench-body">
      <p className="bench-score">
        {(points?.right ?? 0) + (points?.wrong ?? 0) > 0 ? `Right picks ${points?.right ?? 0}.` : "Tap who you think wins. A right pick is 1 fun point."}
        {streak >= 2 && ` On a run of ${streak}${streak >= from - 1 ? ": the next right one is worth 2." : "."}`}
      </p>
      {live && <PickRound state={state} me={me} roundNo={live.no} lineup={live.lineup} heading="This round" sent={sent} />}
      {closed && <PickedRound state={state} me={me} roundNo={closed.no} why={why} />}
      {next && <PickRound state={state} me={me} roundNo={nextNo} lineup={next} heading={`Up next: round ${nextNo}`} sent={sent} />}
      {!live && !closed && !next && (
        <p className="bench-wait">
          {pickLineup(state, nextNo) ? `You play in round ${nextNo}. Pick when you are resting.` : "Picks open when the next round is drawn."}
        </p>
      )}
    </div>
  );
}

/**
 * The bench card: what a player does while they are here. Heckle a court, throw the one curse,
 * pick who wins. Someone on court can still pick the next round if they rest in it. `bare` leaves
 * the heading off, for when the lunch block has taken its room.
 */
export function Bench({ t, state, me, bare }: { t: Tournament; state: TournamentState; me: PlayerId; bare?: boolean }) {
  const [open, setOpen] = useRemembered<Open>("bd.player.bench", OPEN, "none");
  const sent = useSent(t);
  const now = t.now();
  const player = state.players[me];
  const round = currentRound(state);
  const onCourt = Boolean(round && state.clock.phase === "playing" && state.clock.roundNo === round.no && inLineup(round.lineup, me));
  // My game's score is in but the round goes on: the bench opens to me when the round ends.
  const mine = onCourt ? round?.games.find((g) => g.a.includes(me) || g.b.includes(me)) : undefined;
  const later = mine && (mine.score || mine.voided) ? "after the round" : "after game";
  const myCurse = cursesNow(state, now).find((c) => c.by === me);
  const picks = pickRounds(state, me, now);
  const streak = benchPoints(state).get(me)?.streak ?? 0;
  const wait = player?.lastHeckleAt != null ? player.lastHeckleAt + state.config.bench.heckleEveryMs - now : 0;
  const muted = state.bench.muted;
  // an admin's switches: a key that is off goes, and a short line says so where it was
  const cursesOff = Boolean(state.bench.cursesOff);
  const keys = 3 - (muted ? 1 : 0) - (cursesOff ? 1 : 0);
  const shown: Tab | null = open === "none" || (open === "heckle" && muted) || (open === "curse" && cursesOff) ? null : open;
  const offNote = muted && cursesOff ? "Heckles and curses are off for now." : muted ? "Heckles are off for now." : cursesOff ? "Curses are off for now." : null;

  const tab = (id: Tab, label: string, note: string) => (
    <button
      type="button"
      className={`bench-tab bench-${id}` + (shown === id ? " on" : "")}
      aria-expanded={shown === id}
      onClick={() => {
        sent.clear();
        setOpen(shown === id ? "none" : id);
      }}
    >
      <strong>{label}</strong>
      <small>{note}</small>
    </button>
  );

  return (
    <section className="pn-bench">
      <h2 className={bare ? "pn-sr" : undefined}>The bench</h2>
      <div className={"bench-tabs" + (keys === 2 ? " two" : keys === 1 ? " one" : "")}>
        {/* on court, heckles and curses wait: the key says so rather than "ready" */}
        {!muted && tab("heckle", "Heckle", onCourt ? later : wait > 0 ? mmss(wait) : "ready")}
        {!cursesOff && tab("curse", "Curse", myCurse ? mmss(myCurse.leftMs) : player && player.cursesLeft > 0 ? (onCourt ? later : `${player.cursesLeft} left`) : "used")}
        {tab("pick", "Pick", streak >= 2 ? `${streak} in a row` : picks.live || picks.next ? "open" : "closed")}
      </div>
      {offNote && <p className="bench-note">{offNote}</p>}
      {shown === "heckle" && <Heckle state={state} me={me} now={now} sent={sent} onCourt={onCourt} played={later === "after the round"} />}
      {shown === "curse" && <Curse state={state} me={me} now={now} sent={sent} played={later === "after the round"} />}
      {shown === "pick" && <Picks state={state} me={me} now={now} sent={sent} />}
      {sent.refused && (
        <p className="player-refused" role="alert">
          Not sent: {sent.refused}
        </p>
      )}
    </section>
  );
}
